/*
 * -----------------------------------------------------------------------
 *  <copyright file="stacked-column/display.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2022 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { RefObject } from 'react';
import { IOpraColumnSeries, OpraStackingOptions } from 'interfaces/opra-chart.interface';
import { PanelDisplayBy } from 'types/graphql-global-types';
import { IPanelDisplayData } from 'views/components/central-monitoring/panels/panel-display';
import ColumnChart from 'views/components/core/charts/column-chart';

interface StackedColumnResult {
    categories: string[];
    columns: IOpraColumnSeries[];
}

interface GetStackedColumnProps {
    getRef?: (ref: RefObject<any> | null) => void;
    panelState: IPanelDisplayData;
    siteId?: string;
    sites: { [key: string]: any };
    subjects: { [key: string]: any };
}

const getStackedColumn = ({ getRef, panelState, siteId, sites, subjects }: GetStackedColumnProps) => {
    const stackedColumnResult: StackedColumnResult = siteId
        ? panelState?.panelData?.panelData[0]?.stackedColumnForSiteIds[siteId]
        : panelState?.panelData?.panelData[0]?.stackedColumnForStudy;

    const isDataAvailable = () => stackedColumnResult?.columns?.length && stackedColumnResult?.categories?.length;

    if (!isDataAvailable()) return <span>The preview cannot be displayed as data is not available</span>;

    const getCategories = (): string[] => {
        const { categories } = stackedColumnResult;

        switch (panelState.chartConfig.displayBy) {
            case PanelDisplayBy.SITE:
                return categories.map((category: string) => sites[category]?.Fields['Site.name']);
            case PanelDisplayBy.SUBJECT:
                return categories.map((category: string) => subjects[category]?.Fields['Subject.subjectNumber']);
            default:
                return categories ?? [];
        }
    };

    const seriesData: IOpraColumnSeries[] = stackedColumnResult.columns.map((col: IOpraColumnSeries) => ({
        name: col.name,
        color: undefined, // colours are set at chart level in ColumnChart, using a theme
        values: col.values,
        custom: '',
    }));

    return (
        <ColumnChart
            getRef={getRef}
            plotTitle={panelState.chartConfig.plotTitle}
            yAxisTitle={panelState.chartConfig.yTitle}
            xAxisTitle={panelState.chartConfig.xTitle}
            showLegend={!panelState.hideLegend}
            series={seriesData}
            categories={getCategories()}
            stacking={OpraStackingOptions.values}
            type={panelState.chartConfig.type}
            hidePlotTitle={panelState.hidePlotTitle}
        />
    );
};

export { getStackedColumn };
